import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ClipboardCheck, PackagePlus, Pencil, Plus, Printer, Search, Tags, Trash2, Truck } from 'lucide-react';
import { convert, formatMoney, fromBase, type Dimension, type IngredientCategory, type StockHistory, type StockHistoryDay, type StockHistoryLine } from '@bakery/shared';

import { api, type IngredientRow, type StockAlertRow } from '../lib/api.js';
import { printStockHistory } from '../lib/printing.js';
import { IngredientCategoriesModal } from './IngredientCategories.js';
import { EditDeliveryModal } from './EditDelivery.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  MoneyInput,
  NumberInput,
  SectionHeading,
  Select,
  Skeleton,
  Tabs,
  cx,
  useToast,
} from '../components/ui.js';

/**
 * The categories to offer in a picker.
 *
 * Hidden ones are left out, except the one this ingredient is already filed
 * under — otherwise opening an ingredient whose category was since retired
 * would silently re-file it under whatever happened to be first in the list.
 */
function categoryOptions(
  categories: IngredientCategory[] | undefined,
  current?: string,
): { key: string; name: string }[] {
  const all = categories ?? [];
  const options = all.filter((category) => category.isActive || category.key === current);

  if (current && !options.some((category) => category.key === current)) {
    // Filed under something that no longer exists at all: show the raw key
    // rather than dropping it, so nothing is re-filed behind the operator.
    options.push({ id: current, key: current, name: current, color: '', sortOrder: 99, isActive: true, isProtected: false });
  }

  return options.map((category) => ({ key: category.key, name: category.name }));
}

export function InventoryScreen() {
  const [tab, setTab] = useState<'alerts' | 'all' | 'demand' | 'history'>('alerts');
  const [month, setMonth] = useState(() => new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 7));
  const [search, setSearch] = useState('');
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [newIngredientOpen, setNewIngredientOpen] = useState(false);
  const [counting, setCounting] = useState<IngredientRow | null>(null);
  const [editing, setEditing] = useState<IngredientRow | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const ingredients = useQuery({ queryKey: ['ingredients'], queryFn: api.inventory.ingredients });
  const categories = useQuery({ queryKey: ['ingredient-categories', 'all'], queryFn: () => api.inventory.categories(true) });
  const alerts = useQuery({ queryKey: ['stock-alerts'], queryFn: () => api.inventory.alerts() });
  const demand = useQuery({
    queryKey: ['ingredient-demand'],
    queryFn: () => api.analytics.ingredientDemand(),
    enabled: tab === 'demand',
  });
  const history = useQuery({
    queryKey: ['stock-history', month],
    queryFn: () => api.inventory.history(month),
    enabled: tab === 'history',
  });

  const filtered = useMemo(
    () => (ingredients.data ?? []).filter((i) => i.name.toLowerCase().includes(search.toLowerCase())),
    [ingredients.data, search],
  );

  /** Key to display name, so the list shows what the shop called it. */
  const categoryName = useMemo(() => {
    const names = new Map((categories.data ?? []).map((category) => [category.key, category.name]));
    return (key: string) => names.get(key) ?? key.replace(/_/g, ' ');
  }, [categories.data]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Stock room</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
            Ingredients and supplies, counted in real units and priced from what you paid.
          </p>
        </div>
        {/* Wraps on a phone: adding Categories pushed "Receive delivery" —
            the button this screen exists for — off the right edge. */}
        <div className="flex flex-wrap justify-end gap-2">
          {/* Where you look for them: beside the ingredients they group. */}
          <Button variant="ghost" icon={<Tags className="size-4" />} onClick={() => setCategoriesOpen(true)}>
            Categories
          </Button>
          <Button icon={<Plus className="size-4" />} onClick={() => setNewIngredientOpen(true)}>
            Ingredient
          </Button>
          <Button variant="primary" icon={<Truck className="size-4" />} onClick={() => setReceiveOpen(true)}>
            Receive delivery
          </Button>
        </div>
      </header>

      <div className="mb-4 space-y-3">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'alerts', label: 'Needs attention', count: alerts.data?.length ?? 0, tone: 'danger' },
            { value: 'all', label: 'All stock' },
            { value: 'demand', label: 'Buy for the week' },
            { value: 'history', label: 'What came in' },
          ]}
        />
        {tab === 'all' && (
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find an ingredient…" className="pl-9" />
          </div>
        )}
      </div>

      {tab === 'alerts' &&
        (alerts.isLoading ? (
          <Skeleton className="h-40" />
        ) : alerts.data?.length === 0 ? (
          <EmptyState
            icon={<ClipboardCheck className="size-8" />}
            title="Nothing is running low"
            description="Every ingredient is above its reorder point, with days of cover to spare."
          />
        ) : (
          <ul className="space-y-2">
            {alerts.data?.map((alert) => (
              <AlertCard key={alert.ingredientId} alert={alert} />
            ))}
          </ul>
        ))}

      {tab === 'all' &&
        (ingredients.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-16" />
            ))}
          </div>
        ) : (
          <ul className="space-y-1.5">
            {filtered.map((ingredient) => (
              <li key={ingredient.id}>
                {/*
                  * Two actions on one row, so the row itself is not a button —
                  * nesting one inside another is invalid, and the browser picks
                  * a winner you did not choose. Counting stays the whole-row
                  * tap because it is the daily job; editing is its own target.
                  */}
                <div className="card flex items-center gap-1 pr-2 transition focus-within:border-crust-400 hover:border-crust-400">
                  <button
                    onClick={() => setCounting(ingredient)}
                    className="flex min-w-0 flex-1 items-center gap-4 px-4 py-3 text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{ingredient.name}</p>
                      <p className="text-xs text-[var(--color-ink-faint)]">
                        {categoryName(ingredient.group)} · {formatMoney(ingredient.costPerStockUnit)}/{ingredient.stockUnit}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="tabular text-sm font-semibold">
                        {ingredient.qtyDisplay.toLocaleString('en-PH', { maximumFractionDigits: 3 })} {ingredient.stockUnit}
                      </p>
                      <p className="tabular text-xs text-[var(--color-ink-faint)]">
                        reorder at {ingredient.reorderPointDisplay} {ingredient.stockUnit}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={() => setEditing(ingredient)}
                    title={`Edit ${ingredient.name}`}
                    aria-label={`Edit ${ingredient.name}`}
                    className="rounded-[var(--radius-md)] p-2 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]"
                  >
                    <Pencil className="size-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ))}

      {tab === 'demand' && (
        <Card className="p-4">
          <SectionHeading
            title="What the week needs"
            hint="Derived from the bake forecast and each product's recipe."
          />
          {demand.isLoading ? (
            <Skeleton className="h-40" />
          ) : demand.data?.length === 0 ? (
            <EmptyState icon={<PackagePlus className="size-8" />} title="Nothing to buy yet" description="This fills in once there is enough sales history to forecast from." />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line)] text-left text-xs text-[var(--color-ink-soft)]">
                  <th className="pb-2 font-medium">Ingredient</th>
                  <th className="pb-2 text-right font-medium">Needed</th>
                  <th className="pb-2 text-right font-medium">Have</th>
                  <th className="pb-2 text-right font-medium">Short by</th>
                  <th className="pb-2 text-right font-medium">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-line)]">
                {demand.data?.map((row) => (
                  <tr key={row.ingredientId} className={cx(row.shortfallDisplay > 0 && 'text-[var(--color-danger)]')}>
                    <td className="py-2">{row.ingredientName}</td>
                    <td className="tabular py-2 text-right">{round(row.requiredDisplay)} {row.stockUnit}</td>
                    <td className="tabular py-2 text-right">{round(row.onHandDisplay)}</td>
                    <td className="tabular py-2 text-right font-medium">{row.shortfallDisplay > 0 ? round(row.shortfallDisplay) : '—'}</td>
                    <td className="tabular py-2 text-right">{row.shortfallDisplay > 0 ? formatMoney(row.estimatedCost) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === 'history' && (
        <StockHistoryPanel
          month={month}
          onMonth={setMonth}
          data={history.data}
          loading={history.isLoading}
        />
      )}

      {receiveOpen && <ReceiveDeliveryModal onClose={() => setReceiveOpen(false)} />}
      {newIngredientOpen && <NewIngredientModal onClose={() => setNewIngredientOpen(false)} />}
      {counting && <StockCountModal ingredient={counting} onClose={() => setCounting(null)} />}
      {editing && <EditIngredientModal ingredient={editing} onClose={() => setEditing(null)} />}
      {categoriesOpen && <IngredientCategoriesModal onClose={() => setCategoriesOpen(false)} />}
    </div>
  );
}

const round = (value: number) => value.toLocaleString('en-PH', { maximumFractionDigits: 2 });

function AlertCard({ alert }: { alert: StockAlertRow }) {
  const critical = alert.level === 'out' || alert.level === 'critical';
  /*
   * Below zero is not "out". It means the ledger has drifted from the shelf —
   * a delivery never keyed in, or a recipe quantity that is wrong. Buying more
   * will not fix it, so it must not read like a reorder prompt.
   */
  const belowZero = alert.qtyDisplay < 0;

  return (
    <li>
      <Card className={cx('flex items-center gap-4 p-4', critical && 'border-[var(--color-danger)]/40')}>
        <AlertTriangle className={cx('size-5 shrink-0', critical ? 'text-[var(--color-danger)]' : 'text-[var(--color-warn)]')} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{alert.ingredientName}</span>
            <Badge tone={critical ? 'danger' : 'warn'}>
              {belowZero
                ? 'Count is wrong'
                : alert.level === 'out'
                  ? 'Out of stock'
                  : alert.daysOfCover !== null
                    ? `${alert.daysOfCover} days left`
                    : 'Below reorder point'}
            </Badge>
          </div>
          <p className="mt-0.5 text-xs text-[var(--color-ink-soft)]">
            {belowZero ? (
              <>
                Shows {round(alert.qtyDisplay)} {alert.stockUnit} — less than none. Something was used without being
                recorded, or a delivery was never keyed in. Count it and correct it.
              </>
            ) : (
              <>
                {round(alert.qtyDisplay)} {alert.stockUnit} on hand
                {alert.dailyBurnDisplay > 0 && ` · using about ${round(alert.dailyBurnDisplay)} ${alert.stockUnit}/day`}
                {alert.projectedOutOn && ` · runs out ${alert.projectedOutOn}`}
              </>
            )}
          </p>
        </div>

        {belowZero ? (
          /*
           * No order suggestion here. It would be computed from a number the
           * card has just said is wrong, and "Order 25 kg ≈ ₱1,550" is a
           * confident-looking figure resting on nothing.
           */
          <span className="shrink-0 text-right text-xs font-medium text-[var(--color-danger)]">Count first</span>
        ) : (
          alert.suggestedOrderDisplay > 0 && (
            <div className="shrink-0 text-right">
              <p className="tabular text-sm font-semibold">
                Order {round(alert.suggestedOrderDisplay)} {alert.stockUnit}
              </p>
              <p className="tabular text-xs text-[var(--color-ink-faint)]">
                ≈{formatMoney(alert.suggestedOrderCost)}
                {alert.defaultSupplierName && ` · ${alert.defaultSupplierName}`}
              </p>
            </div>
          )
        )}
      </Card>
    </li>
  );
}

/** Receiving supplies: what arrived, in the unit it was bought, at what price. */
/**
 * One line of a delivery.
 *
 * `amount` is centavos, and `priceMode` says what it means: the price of one
 * unit, or what the whole line cost. Both are kept as the one field because a
 * line is priced one way or the other, never both, and two fields would invite
 * the pair to disagree.
 */
interface DeliveryLine {
  ingredientId: string;
  qty: number;
  unit: string;
  priceMode: 'unit' | 'total';
  amount: number;
}

const lineTotalOf = (line: DeliveryLine) =>
  line.priceMode === 'total' ? line.amount : Math.round(line.qty * line.amount);

function ReceiveDeliveryModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const ingredients = useQuery({ queryKey: ['ingredients'], queryFn: api.inventory.ingredients });
  const suppliers = useQuery({ queryKey: ['suppliers'], queryFn: api.inventory.suppliers });
  const units = useQuery({ queryKey: ['units'], queryFn: api.inventory.units });

  const [supplierId, setSupplierId] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<DeliveryLine[]>([]);

  const usable = lines.filter((line) => line.ingredientId && line.qty > 0);
  const total = usable.reduce((sum, line) => sum + lineTotalOf(line), 0);
  const unpriced = usable.filter((line) => lineTotalOf(line) === 0).length;

  const receive = useMutation({
    mutationFn: () =>
      api.inventory.receive({
        supplierId: supplierId || null,
        reference: reference || null,
        /*
         * Whichever way the price was typed is what gets sent, and the server
         * treats that figure as the true one. A sack invoiced at ₱1,550 stays
         * ₱1,550 rather than becoming 25 × the rounded ₱62.
         */
        lines: usable.map((line) => ({
          ingredientId: line.ingredientId,
          qty: line.qty,
          unit: line.unit,
          ...(line.priceMode === 'total' ? { lineTotal: line.amount } : { unitPrice: line.amount }),
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      toast.push({ tone: 'ok', title: 'Delivery recorded', body: `${formatMoney(total)} added to stock` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not record', body: error.message }),
  });

  function addLine() {
    const first = ingredients.data?.[0];
    if (!first) return;
    setLines((current) => [
      ...current,
      { ingredientId: first.id, qty: 1, unit: first.purchaseUnit, priceMode: 'unit', amount: 0 },
    ]);
  }

  const patchLine = (index: number, change: Partial<DeliveryLine>) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...change } : line)));

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Receive a delivery"
      description="Stock and the average cost per unit both update from this."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {/* Guarded on usable lines, not on rows: an added line still
              waiting for a quantity sends nothing, and the server rejects it. */}
          <Button variant="primary" loading={receive.isPending} disabled={usable.length === 0} onClick={() => receive.mutate()}>
            Record {formatMoney(total)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Supplier">
            <Select value={supplierId} onChange={(event) => setSupplierId(event.target.value)}>
              <option value="">Not recorded</option>
              {suppliers.data?.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Reference" hint="Invoice or delivery-receipt number">
            <Input value={reference} onChange={(event) => setReference(event.target.value)} />
          </Field>
        </div>

        <div className="space-y-2">
          {lines.map((line, index) => {
            const ingredient = ingredients.data?.find((i) => i.id === line.ingredientId);
            const compatible = units.data?.byDimension[ingredient?.dimension ?? 'mass'] ?? [];

            const lineTotal = lineTotalOf(line);
            // The figure the operator did not type, shown so the two can never
            // be confused: enter the lot price and see what it works out at.
            const derived =
              line.priceMode === 'total'
                ? line.qty > 0
                  ? `${formatMoney(Math.round(line.amount / line.qty))} per ${line.unit}`
                  : null
                : `${formatMoney(lineTotal)} for the line`;

            return (
              <div key={index} className="rounded-[var(--radius-md)] border border-[var(--color-line)] p-2">
                <div className="grid grid-cols-12 items-end gap-2">
                  <div className="col-span-12 min-w-0 sm:col-span-4">
                    <Select
                      value={line.ingredientId}
                      onChange={(event) => {
                        const next = ingredients.data?.find((i) => i.id === event.target.value);
                        patchLine(index, { ingredientId: event.target.value, unit: next?.purchaseUnit ?? line.unit });
                      }}
                    >
                      {ingredients.data?.map((option) => (
                        <option key={option.id} value={option.id}>{option.name}</option>
                      ))}
                    </Select>
                  </div>

                  <div className="col-span-4 min-w-0 sm:col-span-2">
                    <NumberInput
                      value={line.qty}
                      onChange={(qty) => patchLine(index, { qty })}
                      className="tabular text-right"
                    />
                  </div>

                  <div className="col-span-4 min-w-0 sm:col-span-2">
                    {/* Only units of the ingredient's own dimension: no litres of flour. */}
                    <Select value={line.unit} onChange={(event) => patchLine(index, { unit: event.target.value })}>
                      {compatible.map((unit) => (
                        <option key={unit.code} value={unit.code}>{unit.code}</option>
                      ))}
                    </Select>
                  </div>

                  <div className="col-span-4 min-w-0 sm:col-span-3">
                    <NumberInput
                      placeholder={line.priceMode === 'total' ? '₱ for the lot' : '₱ per unit'}
                      value={line.amount / 100 || ''}
                      onChange={(pesos) => patchLine(index, { amount: Math.round(pesos * 100) })}
                      onClear={() => patchLine(index, { amount: 0 })}
                      className="tabular text-right"
                    />
                  </div>

                  <div className="col-span-12 flex justify-end sm:col-span-1">
                    <button
                      type="button"
                      onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                      title="Remove this line"
                      aria-label="Remove this line"
                      className="rounded-[var(--radius-md)] p-2 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-danger-soft)] hover:text-[var(--color-danger)]"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </div>

                {/*
                  * How the price was meant. A sack of flour is bought as
                  * "25 kg for ₱1,550", not at a price per kilo, and doing that
                  * division by hand is how a delivery gets keyed in wrong.
                  */}
                <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-1 text-xs">
                  <div className="inline-flex overflow-hidden rounded-full border border-[var(--color-line)]">
                    {(['unit', 'total'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => patchLine(index, { priceMode: mode })}
                        className={cx(
                          'px-2.5 py-1 transition',
                          line.priceMode === mode
                            ? 'bg-crust-600 text-white'
                            : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-sunken)]',
                        )}
                      >
                        {mode === 'unit' ? `Per ${line.unit}` : 'For the whole lot'}
                      </button>
                    ))}
                  </div>
                  {line.amount > 0 && derived && (
                    <span className="tabular text-[var(--color-ink-faint)]">= {derived}</span>
                  )}
                </div>
              </div>
            );
          })}

          <Button size="sm" icon={<Plus className="size-4" />} onClick={addLine} disabled={!ingredients.data?.length}>
            Add a line
          </Button>

          {/*
            * Said out loud, because the alternative is worse. A line with no
            * price cannot blend into the average cost — "we do not know what
            * this cost" is not "this cost nothing" — so the stock arrives and
            * the costing stays as it was. Silently doing that would leave the
            * shop wondering why their margins did not move.
            */}
          {unpriced > 0 && (
            <p className="rounded-[var(--radius-md)] bg-[var(--color-warn-soft)] px-3 py-2 text-xs text-[var(--color-warn)]">
              {unpriced === 1 ? 'One line has' : `${unpriced} lines have`} no price. The stock will go in, but the cost
              per unit stays as it is — add the price later and it will catch up on the next delivery.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function StockCountModal({ ingredient, onClose }: { ingredient: IngredientRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [counted, setCounted] = useState(ingredient.qtyDisplay);
  const [note, setNote] = useState('');

  const difference = counted - ingredient.qtyDisplay;

  const save = useMutation({
    mutationFn: () => api.inventory.count(ingredient.id, { countedQty: counted, unit: ingredient.stockUnit, note: note || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      toast.push({ tone: 'ok', title: `${ingredient.name} counted`, body: `Now ${counted} ${ingredient.stockUnit}` });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`Count ${ingredient.name}`}
      description={`System says ${round(ingredient.qtyDisplay)} ${ingredient.stockUnit}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save count</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={`Counted (${ingredient.stockUnit})`}>
          <NumberInput
            autoFocus
            value={counted}
            onChange={setCounted}
            className="tabular text-right text-lg"
          />
        </Field>

        {difference !== 0 && (
          <p
            className={cx(
              'rounded-[var(--radius-md)] px-3 py-2 text-sm',
              difference < 0 ? 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]' : 'bg-[var(--color-ok-soft)] text-[var(--color-ok)]',
            )}
          >
            {difference > 0 ? 'Surplus' : 'Shortfall'} of {round(Math.abs(difference))} {ingredient.stockUnit}. This is
            recorded as an adjustment with your note attached.
          </p>
        )}

        <Field label="Note" hint="Spillage, spoilage, or a miscount earlier in the week.">
          <Input value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Changing an ingredient after it exists.
 *
 * Everything here is a label or a threshold — nothing that would restate what
 * is on the shelf. Stock moves through counting and deliveries, where each
 * change carries a reason; letting it be typed over in an edit form would make
 * the movement ledger a work of fiction.
 */
function EditIngredientModal({ ingredient, onClose }: { ingredient: IngredientRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const units = useQuery({ queryKey: ['units'], queryFn: api.inventory.units });
  const suppliers = useQuery({ queryKey: ['suppliers'], queryFn: api.inventory.suppliers });
  const categories = useQuery({ queryKey: ['ingredient-categories'], queryFn: () => api.inventory.categories() });

  const [form, setForm] = useState({
    name: ingredient.name,
    group: ingredient.group as string,
    stockUnit: ingredient.stockUnit,
    purchaseUnit: ingredient.purchaseUnit,
    reorderPointQty: ingredient.reorderPointDisplay,
    reorderQtyValue: fromBase(ingredient.reorderQty, ingredient.stockUnit),
    defaultSupplierId: ingredient.defaultSupplierId ?? '',
    shelfLifeDays: ingredient.shelfLifeDays ?? ('' as number | ''),
    notes: ingredient.notes ?? '',
    isActive: ingredient.isActive,
  });

  /*
   * Cost is kept apart from the rest of the form.
   *
   * It goes to its own endpoint, behind the margin permission, and is recorded
   * in the movement ledger — so it is not something to bundle into a patch of
   * names and thresholds. `costPerStockUnit` is absent entirely for an account
   * that may not see costs, which is how this tells the two apart.
   */
  const canSeeCost = typeof ingredient.costPerStockUnit === 'number';
  const [cost, setCost] = useState(ingredient.costPerStockUnit ?? 0);
  const [costNote, setCostNote] = useState('');
  const costChanged = canSeeCost && cost !== ingredient.costPerStockUnit;

  // Only units of this ingredient's own dimension. What it is measured in
  // cannot change here at all — see the note under the unit pickers.
  const compatible = units.data?.byDimension[ingredient.dimension] ?? [];

  /*
   * Two steps, because it cannot be undone and the button sits beside Save.
   * The second press is a different button in a different place, so a slip of
   * the finger cannot carry through both.
   */
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.inventory.deleteIngredient(ingredient.id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['ingredient-demand'] });
      toast.push({
        tone: 'ok',
        title: `${ingredient.name} deleted`,
        body: result.movementsRemoved > 0 ? `Its ${result.movementsRemoved} stock movements went with it.` : undefined,
      });
      onClose();
    },
    /*
     * The server's refusal names what is holding the ingredient, so it is
     * shown as written rather than replaced with something vaguer here.
     */
    onError: (error: Error) =>
      toast.push({ tone: 'danger', title: `${ingredient.name} was not deleted`, body: error.message }),
  });

  const save = useMutation({
    mutationFn: async () => {
      await api.inventory.updateIngredient(ingredient.id, {
        name: form.name.trim(),
        group: form.group,
        stockUnit: form.stockUnit,
        purchaseUnit: form.purchaseUnit,
        reorderPointQty: form.reorderPointQty,
        reorderQtyValue: form.reorderQtyValue,
        defaultSupplierId: form.defaultSupplierId || null,
        shelfLifeDays: form.shelfLifeDays === '' ? null : Number(form.shelfLifeDays),
        notes: form.notes.trim() || null,
        isActive: form.isActive,
      });

      /*
       * Only when it actually moved. Sending it every time would write a
       * correction to the ledger each time somebody opened the form and
       * pressed Save, burying the real ones among them.
       */
      if (costChanged) {
        await api.inventory.setIngredientCost(ingredient.id, {
          costPerStockUnit: cost,
          note: costNote.trim() || undefined,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['ingredient-demand'] });
      // Costs feed recipes and margins, so those have to be refetched too.
      if (costChanged) {
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      }
      toast.push({
        tone: 'ok',
        title: `${form.name} saved`,
        body: costChanged ? `Cost set to ${formatMoney(cost)}/${form.stockUnit}.` : undefined,
      });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  /**
   * Switching the stocking unit rewrites the thresholds to match.
   *
   * "Warn below 5" means five kilos; leaving the 5 in place while the unit
   * becomes grams would silently turn a sensible threshold into one that never
   * fires. The stock itself needs no conversion — it is stored in base units.
   */
  function changeStockUnit(next: string) {
    setForm((current) => ({
      ...current,
      stockUnit: next,
      reorderPointQty: Number(convert(current.reorderPointQty, current.stockUnit, next).toFixed(4)),
      reorderQtyValue: Number(convert(current.reorderQtyValue, current.stockUnit, next).toFixed(4)),
    }));
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${ingredient.name}`}
      description="Stock levels are not changed here — count it or receive a delivery for that."
      footer={
        <>
          {/*
            * Delete sits at the far left, away from Save, and asks a second
            * time before it does anything. It cannot be undone, and the two
            * buttons are otherwise a thumb's width apart.
            */}
          {confirmingDelete ? (
            <div className="mr-auto flex items-center gap-2">
              <Button
                variant="danger"
                loading={remove.isPending}
                icon={<Trash2 className="size-4" />}
                onClick={() => remove.mutate()}
              >
                Delete for good
              </Button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="text-xs text-[var(--color-ink-soft)] hover:underline"
              >
                Keep it
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="mr-auto inline-flex items-center gap-1.5 text-sm text-[var(--color-danger)] hover:underline"
            >
              <Trash2 className="size-4" /> Delete
            </button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.name.trim()} onClick={() => save.mutate()}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" required>
            <Input autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field label="Category">
            <Select value={form.group} onChange={(event) => setForm({ ...form, group: event.target.value })}>
              {categoryOptions(categories.data, ingredient.group).map((option) => (
                <option key={option.key} value={option.key}>{option.name}</option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Stocking unit" hint="How you count it">
            <Select value={form.stockUnit} onChange={(event) => changeStockUnit(event.target.value)}>
              {compatible.map((unit) => (
                <option key={unit.code} value={unit.code}>{unit.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Buying unit" hint="How it arrives">
            <Select value={form.purchaseUnit} onChange={(event) => setForm({ ...form, purchaseUnit: event.target.value })}>
              {compatible.map((unit) => (
                <option key={unit.code} value={unit.code}>{unit.name}</option>
              ))}
            </Select>
          </Field>
        </div>

        {/*
          * Measured-in is shown but cannot be changed. Quantities are stored in
          * base units of the dimension, so re-labelling a weight as a volume
          * would leave the number on the shelf meaning litres instead of kilos,
          * with every recipe costed from it quietly wrong and nothing on screen
          * to say so.
          */}
        <p className="rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] px-3 py-2 text-xs text-[var(--color-ink-soft)]">
          Measured by <span className="font-medium">{ingredient.dimension === 'mass' ? 'weight' : ingredient.dimension}</span>,
          which cannot be changed once there is stock against it — the quantity on the shelf would come to mean something
          else. If it is wrong, retire this one and add it again.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`Warn below (${form.stockUnit})`}>
            <NumberInput
              value={form.reorderPointQty}
              onChange={(reorderPointQty) => setForm({ ...form, reorderPointQty })}
              className="tabular text-right"
            />
          </Field>
          <Field label={`Reorder up to (${form.stockUnit})`}>
            <NumberInput
              value={form.reorderQtyValue}
              onChange={(reorderQtyValue) => setForm({ ...form, reorderQtyValue })}
              className="tabular text-right"
            />
          </Field>
        </div>

        {/*
          * What it costs.
          *
          * Normally this arrives by itself — a delivery blends what was paid
          * into the running average, which is the honest way round. But an
          * imported figure can be wrong and a supplier can tell you the new
          * price weeks before the next sack arrives, and until now the only way
          * to fix one was to invent a delivery that never happened.
          *
          * Hidden entirely from an account that may not see costs; the server
          * refuses the change for one too.
          */}
        {canSeeCost && (
          <div className="space-y-3 rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={`Cost per ${form.stockUnit}`}
                hint={
                  costChanged
                    ? `Was ${formatMoney(ingredient.costPerStockUnit)}. Recorded in the stock history.`
                    : 'Usually set by what you paid on the last delivery.'
                }
              >
                <MoneyInput value={cost} onChange={setCost} />
              </Field>

              {costChanged && (
                <Field label="Why" hint="Shown in this ingredient's history beside the change.">
                  <Input
                    value={costNote}
                    onChange={(event) => setCostNote(event.target.value)}
                    placeholder="Supplier put the price up"
                  />
                </Field>
              )}
            </div>

            {costChanged && (
              <p className="text-xs text-[var(--color-warn)]">
                This changes what every recipe using {form.name || 'it'} is reckoned to cost, and the margin on every
                product made from it. Stock on the shelf is not touched.
              </p>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Usual supplier">
            <Select
              value={form.defaultSupplierId}
              onChange={(event) => setForm({ ...form, defaultSupplierId: event.target.value })}
            >
              <option value="">Not set</option>
              {suppliers.data?.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Shelf life (days)" hint="Leave empty if it keeps indefinitely">
            <NumberInput
              inputMode="numeric"
              value={form.shelfLifeDays}
              onChange={(shelfLifeDays) => setForm({ ...form, shelfLifeDays })}
              onClear={() => setForm({ ...form, shelfLifeDays: '' })}
              className="tabular text-right"
            />
          </Field>
        </div>

        <Field label="Notes">
          <Input value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        </Field>

        {/*
          * Retiring rather than deleting. Past deliveries and recipes still
          * point at this row, and removing it would take their history with it.
          */}
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={!form.isActive}
            onChange={(event) => setForm({ ...form, isActive: !event.target.checked })}
            className="mt-0.5 size-4 accent-[var(--color-warn)]"
          />
          <span>
            Retire this ingredient
            <span className="block text-xs text-[var(--color-ink-faint)]">
              Hidden from the stock room and the pickers. Past deliveries and recipes keep it, so nothing in the books
              changes.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}

function NewIngredientModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const units = useQuery({ queryKey: ['units'], queryFn: api.inventory.units });
  const categories = useQuery({ queryKey: ['ingredient-categories'], queryFn: () => api.inventory.categories() });

  const [form, setForm] = useState({
    name: '',
    group: 'other',
    dimension: 'mass' as Dimension,
    stockUnit: 'kg',
    purchaseUnit: 'kg',
    openingQty: 0,
    reorderPointQty: 0,
    reorderQtyValue: 0,
  });

  const compatible = units.data?.byDimension[form.dimension] ?? [];

  const create = useMutation({
    mutationFn: () => api.inventory.createIngredient(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      toast.push({ tone: 'ok', title: `${form.name} added` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not add', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="New ingredient"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!form.name} onClick={() => create.mutate()}>
            Add ingredient
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {/*
          * Category belongs at the point of creation. Without it every new
          * ingredient silently landed in "Other" and had to be re-filed from
          * the edit form afterwards.
          */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" required>
            <Input autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field label="Category">
            <Select value={form.group} onChange={(event) => setForm({ ...form, group: event.target.value })}>
              {categoryOptions(categories.data, form.group).map((option) => (
                <option key={option.key} value={option.key}>{option.name}</option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Measured in">
            <Select
              value={form.dimension}
              onChange={(event) => {
                const dimension = event.target.value as Dimension;
                const first = units.data?.byDimension[dimension]?.[0]?.code ?? 'kg';
                setForm({ ...form, dimension, stockUnit: first, purchaseUnit: first });
              }}
            >
              <option value="mass">Weight</option>
              <option value="volume">Volume</option>
              <option value="count">Count</option>
            </Select>
          </Field>
          <Field label="Stocking unit" hint="How you count it">
            <Select value={form.stockUnit} onChange={(event) => setForm({ ...form, stockUnit: event.target.value })}>
              {compatible.map((unit) => (
                <option key={unit.code} value={unit.code}>{unit.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Buying unit" hint="How it arrives">
            <Select value={form.purchaseUnit} onChange={(event) => setForm({ ...form, purchaseUnit: event.target.value })}>
              {compatible.map((unit) => (
                <option key={unit.code} value={unit.code}>{unit.name}</option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={`Opening stock (${form.stockUnit})`}>
            <NumberInput value={form.openingQty} onChange={(openingQty) => setForm({ ...form, openingQty })} className="tabular text-right" />
          </Field>
          <Field label="Warn below">
            <NumberInput value={form.reorderPointQty} onChange={(reorderPointQty) => setForm({ ...form, reorderPointQty })} className="tabular text-right" />
          </Field>
          <Field label="Reorder up to">
            <NumberInput value={form.reorderQtyValue} onChange={(reorderQtyValue) => setForm({ ...form, reorderQtyValue })} className="tabular text-right" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

/**
 * What came in, day by day.
 *
 * The shop has always recorded this — every delivery, every line, every price —
 * and never showed it back. The stock room could say what was on the shelf and
 * not how it got there, and Expenses carried the month as a single figure with
 * no way to open it.
 *
 * Deliveries and corrections share a day but not a meaning, so they share a
 * list and not a style: a delivery is money leaving, a count or a write-off is
 * the shelf being told the truth. Only days something happened are listed. A
 * stock room has nothing to say about a quiet Tuesday.
 */
function StockHistoryPanel({
  month,
  onMonth,
  data,
  loading,
}: {
  month: string;
  onMonth: (month: string) => void;
  data: StockHistory | undefined;
  loading: boolean;
}) {
  /* The delivery being corrected, if any. */
  const [editing, setEditing] = useState<StockHistoryLine | null>(null);

  const shift = (by: number) => {
    const [year, index] = month.split('-').map(Number);
    onMonth(new Date(Date.UTC(year!, index! - 1 + by, 1)).toISOString().slice(0, 7));
  };

  const label = useMemo(
    () => new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-PH', { month: 'long', year: 'numeric' }),
    [month],
  );

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <SectionHeading
            title="What came in"
            hint={
              data
                ? `${label} · ${data.deliveries} ${data.deliveries === 1 ? 'delivery' : 'deliveries'} · ${formatMoney(data.spent)}`
                : label
            }
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" aria-label="Previous month" onClick={() => shift(-1)}>‹</Button>
          <Input type="month" value={month} onChange={(event) => onMonth(event.target.value)} className="w-40" />
          <Button size="sm" variant="ghost" aria-label="Next month" onClick={() => shift(1)}>›</Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<Printer className="size-4" />}
            disabled={!data || data.days.length === 0}
            onClick={() => data && printStockHistory(data, label)}
          >
            Print
          </Button>
        </div>
      </div>

      {loading && <Skeleton className="h-40" />}

      {data && data.days.length === 0 && (
        <EmptyState
          icon={<PackagePlus className="size-8" />}
          title="Nothing came in"
          description={`No deliveries, counts or write-offs were recorded in ${label}.`}
        />
      )}

      {data && data.days.length > 0 && (
        <div className="space-y-5">
          {data.days.map((day) => (
            <StockHistoryDayBlock key={day.date} day={day} onEdit={setEditing} />
          ))}
        </div>
      )}

      {editing && <EditDeliveryModal line={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function StockHistoryDayBlock({ day, onEdit }: { day: StockHistoryDay; onEdit: (line: StockHistoryLine) => void }) {
  const heading = new Date(`${day.date}T12:00:00Z`).toLocaleDateString('en-PH', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3 border-b border-[var(--color-line)] pb-1.5">
        <h3 className="font-display text-sm font-semibold">{heading}</h3>
        <span className="tabular text-sm font-semibold">
          {day.spent > 0 ? formatMoney(day.spent) : <span className="font-normal text-[var(--color-ink-faint)]">no spend</span>}
        </span>
      </div>

      {/*
        * Fixed columns, shared by every day.
        *
        * Each day is its own table — they have to be, since a date heading and
        * a day's total sit between them — and an auto-width table sizes its
        * columns from its own rows alone. So a day holding "Croissant choco
        * (frozen)" pushed its numbers left while the day under it did not, and
        * the quantities zig-zagged down the page. Naming the widths here makes
        * every day measure the same.
        */}
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col />
          <col className="w-24" />
          <col className="w-28" />
          <col className="w-28" />
        </colgroup>
        <tbody className="divide-y divide-[var(--color-line)]">
          {day.lines.map((line, index) => (
            <tr key={`${line.ingredientId}-${line.at}-${index}`}>
              <td className="py-1.5 pr-2 break-words">
                {/*
                  * A delivery is the one kind of row that can be wrong in a way
                  * worth fixing — a count is corrected by counting again. So
                  * only those are a button.
                  */}
                {line.kind === 'delivery' && line.lineId ? (
                  <button
                    type="button"
                    onClick={() => onEdit(line)}
                    title={`Correct this ${line.ingredientName} delivery`}
                    className="text-left underline decoration-[var(--color-line-strong)] decoration-dotted underline-offset-4 hover:decoration-[var(--color-ink)]"
                  >
                    {line.ingredientName}
                  </button>
                ) : (
                  line.ingredientName
                )}
                {line.kind !== 'delivery' && (
                  <Badge tone="neutral" className="ml-2 align-middle">
                    {line.kind === 'count' ? 'counted' : 'written off'}
                  </Badge>
                )}
                {line.editedAt && (
                  <span className="ml-2 align-middle" title={`Corrected ${line.editedAt}`}>
                    <Badge tone="neutral">edited</Badge>
                  </span>
                )}
                {(line.supplier || line.reference || line.note) && (
                  <span className="block text-xs text-[var(--color-ink-faint)]">
                    {[line.supplier, line.reference, line.note].filter(Boolean).join(' · ')}
                  </span>
                )}
              </td>
              <td className="tabular py-1.5 text-right whitespace-nowrap">
                {line.qty > 0 && line.kind !== 'delivery' ? '+' : ''}
                {round(line.qty)} {line.unit}
              </td>
              <td className="tabular py-1.5 text-right whitespace-nowrap text-[var(--color-ink-soft)]">
                {line.unitPrice != null ? `${formatMoney(line.unitPrice)}/${line.unit}` : '—'}
              </td>
              <td className="tabular py-1.5 text-right font-medium whitespace-nowrap">
                {line.lineTotal != null ? (
                  formatMoney(line.lineTotal)
                ) : line.kind === 'delivery' ? (
                  /* Received, but no price was keyed in — not the same as free. */
                  <span className="font-normal text-[var(--color-ink-faint)]">not priced</span>
                ) : (
                  <span className="font-normal text-[var(--color-ink-faint)]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
